/**
 * <search-dialog> — header search overlay with predictive results.
 *
 * Opens on [data-search-open] clicks (buttons live outside this element, in the
 * header). Closes on [data-search-close], overlay click, or ESC.
 * Predictive results come from Shopify's /search/suggest.json endpoint,
 * debounced. Submitting the form goes to the full /search results page.
 */
class SearchDialog extends globalThis.ThemeElement {
  connectedCallback() {
    this.panel = this.$('[data-search-panel]');
    this.overlay = this.$('[data-search-overlay]');
    this.input = this.$('[data-search-input]');
    this.results = this.$('[data-search-results]');
    this.form = this.$('form');
    this._releaseFocus = null;

    /* Open triggers live in the header, outside this element → document scope. */
    document.querySelectorAll('[data-search-open]').forEach((btn) => {
      this.on(btn, 'click', (e) => { e.preventDefault(); this.open(); });
    });
    this.$$('[data-search-close]').forEach((btn) => {
      this.on(btn, 'click', (e) => { e.preventDefault(); this.close(); });
    });
    if (this.overlay) this.on(this.overlay, 'click', () => this.close());
    this.on(window, 'keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); });

    const debounced = globalThis.JunneTheme.debounce(() => this.suggest(), 220);
    if (this.input) this.on(this.input, 'input', debounced);

    this.root = (globalThis.Shopify && globalThis.Shopify.routes && globalThis.Shopify.routes.root) || '/';
  }

  get isOpen() { return this.panel && this.panel.getAttribute('aria-hidden') === 'false'; }

  open() {
    if (!this.panel || this.isOpen) return;
    this.panel.removeAttribute('hidden');
    this.panel.setAttribute('aria-hidden', 'false');
    this.panel.style.transform = 'translateY(0)';
    if (this.overlay) {
      this.overlay.classList.remove('hidden');
      this.overlay.setAttribute('aria-hidden', 'false');
    }
    document.body.classList.add('search-open');
    globalThis.JunneTheme.lockBodyScroll();
    this._releaseFocus = globalThis.JunneTheme.trapFocus(this.panel);
    /* focus the input after the open transition starts */
    setTimeout(() => this.input && this.input.focus(), 60);
  }

  close() {
    if (!this.panel || !this.isOpen) return;
    this.panel.setAttribute('aria-hidden', 'true');
    this.panel.style.transform = 'translateY(-100%)';
    if (this.overlay) {
      this.overlay.classList.add('hidden');
      this.overlay.setAttribute('aria-hidden', 'true');
    }
    document.body.classList.remove('search-open');
    globalThis.JunneTheme.unlockBodyScroll();
    this._releaseFocus && this._releaseFocus();
    this._releaseFocus = null;
    setTimeout(() => { if (!this.isOpen) this.panel.setAttribute('hidden', ''); }, 300);
  }

  async suggest() {
    const term = (this.input.value || '').trim();
    if (term.length < 2) {
      this.results.hidden = true;
      this.results.innerHTML = '';
      return;
    }
    try {
      const url = new URL(`${this.root}search/suggest.json`, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'product,collection,query');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(`suggest ${res.status}`);
      const data = await res.json();
      this.render(data.resources?.results || {}, term);
    } catch (err) {
      console.error('search suggest', err);
    }
  }

  render(results, term) {
    const products = results.products || [];
    const collections = results.collections || [];
    const queries = results.queries || [];
    if (!products.length && !collections.length && !queries.length) {
      this.results.hidden = false;
      this.results.innerHTML = `<p class="text-sm text-[color:var(--color-muted-foreground)] py-4">No results for “${this.esc(term)}”. Press Enter to search anyway.</p>`;
      return;
    }

    /* suggest.json returns price as a rupee string e.g. "2500.00". */
    const money = (val) => {
      const n = parseFloat(val);
      if (isNaN(n)) return '';
      return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
    };

    const eyebrow = 'font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:var(--color-muted-foreground);margin-bottom:.75rem;';
    let html = '<div style="display:grid;grid-template-columns:1fr;gap:1.5rem;" class="search-results-grid">';

    /* Products block */
    html += '<div style="grid-column:1 / -1;">';
    if (products.length) {
      html += `<p style="${eyebrow}">Products</p>`;
      html += '<ul style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:1rem;list-style:none;padding:0;margin:0;" class="search-results-products">';
      for (const p of products.slice(0, 6)) {
        const img = p.image || (p.featured_image && p.featured_image.url) || '';
        const price = p.price != null ? money(p.price) : '';
        const imgUrl = img ? (img.includes('?') ? `${img}&width=300` : `${img}?width=300`) : '';
        html += `
          <li>
            <a href="${p.url}" class="group" style="display:block;text-decoration:none;color:inherit;">
              <div style="aspect-ratio:4/5;overflow:hidden;background:var(--color-muted);margin-bottom:.5rem;border-radius:2px;">
                ${imgUrl ? `<img src="${imgUrl}" alt="${this.esc(p.title)}" style="width:100%;height:100%;object-fit:cover;" loading="lazy">` : ''}
              </div>
              <p style="font-size:.8125rem;line-height:1.2;">${this.esc(p.title)}</p>
              ${price ? `<p style="font-size:.75rem;color:var(--color-muted-foreground);margin-top:.125rem;">${price}</p>` : ''}
            </a>
          </li>`;
      }
      html += '</ul>';
    }
    html += '</div>';

    /* Suggestions row */
    if (collections.length || queries.length) {
      html += '<div style="display:flex;flex-wrap:wrap;gap:2.5rem;">';
      if (collections.length) {
        html += `<div><p style="${eyebrow}">Collections</p><ul style="list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:.5rem;">`;
        for (const c of collections.slice(0, 5)) {
          html += `<li><a href="${c.url}" style="font-size:.875rem;text-decoration:none;color:inherit;">${this.esc(c.title)}</a></li>`;
        }
        html += '</ul></div>';
      }
      if (queries.length) {
        html += `<div><p style="${eyebrow}">Suggestions</p><ul style="list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:.5rem;">`;
        for (const q of queries.slice(0, 5)) {
          html += `<li><a href="${q.url}" style="font-size:.875rem;text-decoration:none;color:inherit;">${this.esc(q.text)}</a></li>`;
        }
        html += '</ul></div>';
      }
      html += '</div>';
    }

    html += '</div>';
    this.results.hidden = false;
    this.results.innerHTML = html;
  }

  esc(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
}

if (!customElements.get('search-dialog')) customElements.define('search-dialog', SearchDialog);
